import { lossFractionToPercent } from '../../../components/graphs/MonthlyAverageSoilingLoss';

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function numberOrNull(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatIsoMonthDay(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? '').trim());
  if (!match) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${MONTH_SHORT[month - 1]} ${day}`;
}

function formatPercent(fraction) {
  const pct = lossFractionToPercent(fraction);
  return pct == null ? '—' : `${pct.toFixed(1)}%`;
}

function formatSignedPts(pts) {
  const n = numberOrNull(pts);
  if (n == null) return null;
  const abs = Math.abs(n).toFixed(1);
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return abs;
}

function formatGwh(value) {
  const n = numberOrNull(value);
  if (n == null) return '—';
  const digits = Math.abs(n) >= 10 ? 1 : 2;
  return `${n.toFixed(digits)} GWh`;
}

function formatMm(value) {
  const n = numberOrNull(value);
  if (n == null) return null;
  return Number.isInteger(n) ? `${n} mm` : `${n.toFixed(1)} mm`;
}

function daysFooterClass(days) {
  if (days == null) return '';
  if (days >= 14) return 'red';
  if (days >= 7) return 'yellow';
  return 'green';
}

export function mapSoilingMetricsToCards(payload) {
  const current = payload?.current ?? {};
  const energy = payload?.energyLossT30d ?? {};
  const rain = payload?.daysSinceCleaningRain ?? {};
  const forecast = payload?.forecastPeak14d ?? {};

  const changePts = formatSignedPts(current.hsuChangePtsSinceLastRain);
  const lastRainDay = formatIsoMonthDay(rain.lastRainDay);
  const lastRainMm = formatMm(rain.lastRainMm);
  const nextRainDay = formatIsoMonthDay(forecast.nextCleaningRainDay);
  const nextRainMm = formatMm(forecast.nextCleaningRainMm);
  const days = numberOrNull(rain.days);
  const daysValue = days == null ? '—' : String(Math.round(days));

  let lastResetFooter = 'last reset: —';
  if (lastRainDay && lastRainMm) {
    lastResetFooter = `last reset: ${lastRainDay}, ${lastRainMm}`;
  } else if (lastRainMm) {
    lastResetFooter = `last reset: ${lastRainMm} event`;
  } else if (lastRainDay) {
    lastResetFooter = `last reset: ${lastRainDay}`;
  }

  let forecastFooter = 'no cleaning rain forecast';
  if (forecast.hasCleaningRainForecast && nextRainDay && nextRainMm) {
    forecastFooter = `cleaning rain ${nextRainDay} (${nextRainMm})`;
  } else if (forecast.hasCleaningRainForecast && nextRainDay) {
    forecastFooter = `cleaning rain ${nextRainDay}`;
  }

  return {
    current: {
      value: formatPercent(current.hsuLossFraction),
      footer: changePts != null ? `↝ ${changePts}pts since last rain` : '↝ — since last rain',
      footerClass: changePts != null ? 'green' : '',
    },
    energy: {
      value: formatGwh(energy.hsuEnergyLossGwh),
      footer: 'vs clean-module baseline',
      footerClass: energy.hsuEnergyLossGwh != null ? 'yellow' : '',
    },
    days: {
      value: daysValue,
      footer: lastResetFooter,
      footerClass: daysFooterClass(days),
    },
    forecast: {
      value: formatPercent(forecast.peakHsuLossFraction),
      footer: forecastFooter,
      footerClass: forecast.hasCleaningRainForecast ? 'green' : '',
    },
  };
}
